import {
  Booking,
  BookingListResponse,
  ApiBookingListResponse,
  BookingQueryParams,
  CreateBookingDto,
  UpdateBookingDto,
} from './bookings.dto';
import { logging, API_BASE_URL } from '@/config/env';

// Custom logging function
const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class BookingsService {
  private baseUrl = API_BASE_URL;
  private logoutCallback: (() => void) | null = null;

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      // Try to parse error response to check for session expired or extract message
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }

        // Extract error message - handle both string and array formats
        if (errorData.message) {
          const errorMessage = Array.isArray(errorData.message)
            ? errorData.message.join(', ')
            : errorData.message;
          throw new Error(errorMessage);
        }

        // Fallback to error property if message doesn't exist
        if (errorData.error?.message) {
          const errorMessage = Array.isArray(errorData.error.message)
            ? errorData.error.message.join(', ')
            : errorData.error.message;
          throw new Error(errorMessage);
        }
      } catch (error) {
        // If it's already an Error we threw, rethrow it
        if (error instanceof Error && error.message !== errorText) {
          throw error;
        }
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    // Check for session expired in successful response
    if (
      data.status === false &&
      data.message === 'Invalid or expired admin session'
    ) {
      customLog('Session expired in response, triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
    }

    return data;
  }

  // Booking Management APIs
  async getBookings(
    token: string,
    params: BookingQueryParams = {}
  ): Promise<BookingListResponse> {
    customLog('API Service: getBookings called');
    customLog('API Service: Token:', token ? 'Present' : 'Missing');
    customLog('API Service: Params:', params);

    const queryString = new URLSearchParams();

    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.status !== undefined)
      queryString.append('status', params.status.toString());
    if (params.dashboard !== undefined)
      queryString.append('dashboard', params.dashboard ? 'true' : 'false');
    if (params.exportData !== undefined)
      queryString.append('exportData', params.exportData ? 'true' : 'false');
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);
    if (params.startDate) queryString.append('startDate', params.startDate);
    if (params.endDate) queryString.append('endDate', params.endDate);

    const endpoint = `/admin/bookings${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    customLog('API Service: Final endpoint:', endpoint);
    const response = await this.request<ApiBookingListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    customLog('=== API Service Response Analysis ===');
    customLog('Full response object:', response);
    customLog('response.data:', response.data);

    // Handle response structure
    if (!response.data) {
      customLog('API Service: Warning - response.data is undefined');
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(response.data)) {
      const result = {
        data: response.data,
        meta: {
          total: response.total || 0,
          page: response.page || 1,
          lastPage: response.lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure if needed
    const result = {
      data: Array.isArray(response.data) ? response.data : [],
      meta: {
        total: response.total || 0,
        page: response.page || 1,
        lastPage: response.lastPage || 1,
        limit: params.limit || 10,
      },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async getBooking(token: string, id: number): Promise<Booking> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: Booking;
    }>(`/admin/bookings/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async createBooking(
    token: string,
    bookingData: CreateBookingDto
  ): Promise<Booking> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: Booking;
    }>('/admin/bookings', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bookingData),
    });
    return response.data;
  }

  async updateBooking(
    token: string,
    id: number,
    bookingData: UpdateBookingDto
  ): Promise<Booking> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: Booking;
    }>(`/admin/bookings/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bookingData),
    });
    return response.data;
  }

  async deleteBooking(token: string, id: number): Promise<{ message: string }> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data?: unknown;
    }>(`/admin/bookings/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { message: response.message };
  }

  async downloadReport(token: string, bookingId: number): Promise<Blob> {
    customLog('API Booking: downloadReport called for booking:', bookingId);

    const url = `${this.baseUrl}/admin/bookings/${bookingId}/report/download`;

    customLog('=== Downloading Report ===');
    customLog('URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      // Try to parse error response to check for session expired or extract message
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }

        // Extract error message - handle both string and array formats
        if (errorData.message) {
          const errorMessage = Array.isArray(errorData.message)
            ? errorData.message.join(', ')
            : errorData.message;
          throw new Error(errorMessage);
        }

        // Fallback to error property if message doesn't exist
        if (errorData.error?.message) {
          const errorMessage = Array.isArray(errorData.error.message)
            ? errorData.error.message.join(', ')
            : errorData.error.message;
          throw new Error(errorMessage);
        }
      } catch (error) {
        // If it's already an Error we threw, rethrow it
        if (error instanceof Error && error.message !== errorText) {
          throw error;
        }
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    customLog('Success response: PDF blob received');

    return blob;
  }

  async uploadReport(
    token: string,
    bookingId: number,
    formData: FormData
  ): Promise<{ message: string }> {
    customLog('API Booking: uploadReport called for booking:', bookingId);

    const url = `${this.baseUrl}/admin/bookings/${bookingId}/report/upload`;

    customLog('=== Uploading Report ===');
    customLog('URL:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
      body: formData,
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      // Try to parse error response to check for session expired or extract message
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }

        // Extract error message - handle both string and array formats
        if (errorData.message) {
          const errorMessage = Array.isArray(errorData.message)
            ? errorData.message.join(', ')
            : errorData.message;
          throw new Error(errorMessage);
        }

        // Fallback to error property if message doesn't exist
        if (errorData.error?.message) {
          const errorMessage = Array.isArray(errorData.error.message)
            ? errorData.error.message.join(', ')
            : errorData.error.message;
          throw new Error(errorMessage);
        }
      } catch (error) {
        // If it's already an Error we threw, rethrow it
        if (error instanceof Error && error.message !== errorText) {
          throw error;
        }
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Success response:', data);

    return { message: data.message };
  }
}

// Export a singleton instance
export const bookingsService = new BookingsService();
